import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";

const SECTIONS = [
  { key: "products", label: "Produk" },
  { key: "devices", label: "Merek & Tipe HP" },
  { key: "categories", label: "Kategori" },
  { key: "settings", label: "Pengaturan Toko" },
];

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", permissions: [], active: true });
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ email: "", name: "", password: "", permissions: [], active: true }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name || "", password: "", permissions: u.permissions || [], active: u.active !== false }); setOpen(true); };
  const togglePerm = (k) => setForm((f) => ({ ...f, permissions: f.permissions.includes(k) ? f.permissions.filter((x) => x !== k) : [...f.permissions, k] }));

  const save = async () => {
    if (!form.email.trim()) return toast.error("Email wajib diisi");
    if (!editing && (!form.password || form.password.length < 6)) return toast.error("Password minimal 6 karakter");
    try {
      if (editing) await api.put(`/admin/users/${editing.id}`, form);
      else await api.post("/admin/users", form);
      toast.success("Akun disimpan"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/admin/users/${toDelete.id}`); toast.success("Akun dihapus"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus"); }
    finally { setToDelete(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Manajemen User</h1>
          <p className="mt-1 text-sm text-muted-foreground">Buat akun admin & atur batasan aksesnya.</p>
        </div>
        <button data-testid="add-user-btn" onClick={openNew} className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> Tambah Admin</button>
      </div>

      <div className="mt-6 space-y-2">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{u.name || u.email}</span>
                {u.role === "owner" ? <span className="flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-xs font-semibold text-accent"><ShieldCheck className="h-3 w-3" /> Pemilik</span> : <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">Admin</span>}
                {u.active === false && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-700">Nonaktif</span>}
              </div>
              <p className="text-xs text-muted-foreground truncate">{u.email} · {u.role === "owner" ? "Semua akses" : (u.permissions?.length ? u.permissions.join(", ") : "tanpa izin bagian")}</p>
            </div>
            {u.role !== "owner" && (
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(u)} className="rounded-lg p-2 hover:bg-secondary" data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setToDelete(u)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Admin" : "Tambah Admin"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Email</Label><Input data-testid="user-email-input" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>{editing ? "Password baru (kosongkan jika tetap)" : "Password"}</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Izin Akses Bagian</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {SECTIONS.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 rounded-lg border border-border p-2 text-sm">
                    <Checkbox className="rounded-[4px]" checked={form.permissions.includes(s.key)} onCheckedChange={() => togglePerm(s.key)} data-testid={`perm-${s.key}`} /> {s.label}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Dashboard selalu dapat diakses. Manajemen user hanya untuk pemilik.</p>
            </div>
            <div className="flex items-center justify-between"><Label>Akun Aktif</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
          </div>
          <DialogFooter><button data-testid="save-user-btn" onClick={save} className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Simpan</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus akun admin?</AlertDialogTitle><AlertDialogDescription>Akun "{toDelete?.email}" akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-user" onClick={confirmDelete} className="bg-destructive">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
